import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PiBellSimpleRingingDuotone } from "react-icons/pi";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useWindowScroll } from "react-use";
import { ActionIcon, Tooltip } from "rizzui";
import logo from "../../../assets/attiva_logo.png";
import fullLogo from "../../../assets/logo_completo.png";
import CogSolidIcon from "../../../components/icons/cog-solid";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useCommunicationAlerts } from "../../../contexts/CommunicationAlertsContext";
import { useIsMounted } from "../../../hooks/use-is-mounted";
import { RootState } from "../../../redux/store";
import { attivaMenuItems, menuRoutes } from "../../../router/menu";
import cn from "../../../utils/classNames";
import Sidebar from "../_sidebar/sidebar";
import HamburgerButton from "../settings/hamburger-button";
import ProfileMenu from "../settings/profile-menu";
import HeaderMenuLeft from "./header-menu-left";


type StickyHeaderProps = {
    className?: string;
    offset?: number;
};

function StickyHeader({
    offset = 2,
    className,
    children,
}: React.PropsWithChildren<StickyHeaderProps>) {

    const isMounted = useIsMounted();
    const windowScroll = useWindowScroll();
    return (
        <header
            className={cn(
                "sticky top-0 z-[9999] flex items-center bg-gray-0/80 p-4 backdrop-blur-xl dark:bg-gray-50/50 md:px-5 lg:px-6",
                ((isMounted && windowScroll.y) as number) > offset ? "card-shadow" : "",
                className
            )}
            style={{ boxShadow: '0px 0px 10px rgba(0, 0, 0, .1)' }}
        >
            {children}
        </header>
    );
}

function HeaderMenuRight() {

    const { userCan } = usePrivilege();
    const navigate = useNavigate();
    const { hasPending, isEligible, pendingCount } = useCommunicationAlerts();
    const canOpenCommunicationsPage = userCan('list_communications', 'communications');
    const [isBellAnimating, setIsBellAnimating] = useState(false);

    useEffect(() => {
        if (!isEligible || !hasPending) {
            setIsBellAnimating(false);
            return;
        }

        let timeoutId: ReturnType<typeof window.setTimeout> | null = null;
        const intervalId = window.setInterval(() => {
            setIsBellAnimating(true);
            timeoutId = window.setTimeout(() => setIsBellAnimating(false), 1400);
        }, 5000);

        return () => {
            window.clearInterval(intervalId);
            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [hasPending, isEligible]);

    return (
        <div className="ms-auto flex shrink-0 items-center gap-2 text-gray-700 xs:gap-3 xl:gap-4">
            {/* <MessagesDropdown>
        <ActionIcon
          aria-label="Messages"
          variant="text"
          className={cn(
            ' relative h-[34px] w-[34px] overflow-hidden rounded-full md:h-9 md:w-9 3xl:h-10 3xl:w-10 '
          )}
        >
          <PiChatsCircleDuotone className="h-6 w-auto" />
          <Badge
            renderAsDot
            color="success"
            enableOutlineRing
            className="absolute right-1 top-2.5 -translate-x-1 -translate-y-1/4"
          />
        </ActionIcon>
      </MessagesDropdown>

      <NotificationDropdown>
        <ActionIcon
          aria-label="Notification"
          variant="text"
          className={cn(
            'relative h-[34px] w-[34px] overflow-hidden rounded-full md:h-9 md:w-9 3xl:h-10 3xl:w-10'
          )}
        >
          <PiBellSimpleRingingDuotone className="h-6 w-auto" />
          <Badge
            renderAsDot
            color="warning"
            enableOutlineRing
            className="absolute right-1 top-2.5 -translate-x-1 -translate-y-1/4"
          />
        </ActionIcon>
      </NotificationDropdown>
*/}

            {isEligible && hasPending && (
                <Tooltip
                    content={hasPending
                        ? `Tienes ${pendingCount} comunicación${pendingCount === 1 ? '' : 'es'} pendiente${pendingCount === 1 ? '' : 's'} de cierre en tu departamento`
                        : 'Comunicaciones pendientes de cierre de tu departamento'}
                    placement="bottom"
                >
                    <ActionIcon
                        aria-label="Comunicaciones pendientes"
                        variant="text"
                        className={cn(
                            'relative h-[30px] w-[30px] shadow backdrop-blur-md dark:bg-gray-100 md:h-9 md:w-9',
                            canOpenCommunicationsPage ? 'cursor-pointer' : 'cursor-default'
                        )}
                        onClick={() => {
                            if (!canOpenCommunicationsPage) {
                                return;
                            }

                            navigate(menuRoutes.calls.path + menuRoutes.calls.communications);
                        }}
                    >
                        <motion.span
                            className="flex items-center justify-center"
                            animate={isBellAnimating ? { rotate: [0, 18, -14, 10, -6, 0] } : { rotate: 0 }}
                            transition={{ duration: 1.1, ease: 'easeInOut' }}
                            style={{ transformOrigin: 'top center' }}
                        >
                            <PiBellSimpleRingingDuotone
                                className={cn('h-[20px] w-auto', hasPending ? 'text-amber-600' : 'text-gray-700')}
                            />
                        </motion.span>
                        {hasPending && (
                            <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-red-600 px-1 text-center text-[10px] font-semibold leading-[18px] text-white">
                                {pendingCount > 99 ? '99+' : pendingCount}
                            </span>
                        )}
                    </ActionIcon>
                </Tooltip>
            )}

            { userCan("manage_company_configuration", "user") && (
                <ActionIcon
                    aria-label="AJUSTES"
                    variant="text"
                    className={cn(
                        'relative h-[30px] w-[30px] shadow backdrop-blur-md dark:bg-gray-100 md:h-9 md:w-9',
                    )}
                    onClick={() => {
                        if (window.location.pathname.includes(menuRoutes.settings.path)) {
                            navigate(menuRoutes.dashboard.path);
                        } else {
                            navigate(menuRoutes.settings.path + menuRoutes.settings.contracts)
                        }
                    }}
                >
                    <CogSolidIcon
                        strokeWidth={1.8}
                        className="h-[22px] w-auto"
                    />
                </ActionIcon>
            )}

            <ProfileMenu
                buttonClassName="w-auto sm:w-auto p-1"
                avatarClassName="!w-7 !h-7 sm:!h-8 sm:!w-8"
            />
        </div>
    );
}

export default function Header() {

    const [lastUserId, setLastUserId] = useState("");
    const user = useSelector((state: RootState) => state.auth.user);
    const location = useLocation();

    useEffect(() => {
        if (user && user?.id !== lastUserId) {
            setLastUserId(user.id);
        }

        return () => {
            setLastUserId("");
        }
    }, [user])


    return (
        <StickyHeader
            className={"z-[990] justify-between 2xl:py-5 2xl:pl-6  3xl:px-8"}
        >
            <div className="hidden items-center gap-3 xl:flex">
                <Link
                    aria-label="Site Logo"
                    to={"/"}
                    className="me-4 hidden w-[155px] shrink-0 text-gray-800 hover:text-gray-900 lg:me-5 xl:block"
                >
                    <img src={fullLogo} width={"130px"} alt="Logo de Attiva" />
                </Link>
                <HeaderMenuLeft pathname={location.pathname} key={lastUserId} menuItems={attivaMenuItems} />
            </div>
            <div className="flex w-full items-center gap-5 xl:w-auto 3xl:gap-6">
                <div className="flex w-full max-w-2xl items-center xl:w-auto">
                    <HamburgerButton
                        view={<Sidebar className="static w-full 2xl:w-full" />}
                    />
                    <Link
                        aria-label="Site Logo"
                        to={"/"}
                        className="me-4 w-9 shrink-0 text-gray-800 hover:text-gray-900 lg:me-5 xl:hidden"
                    >
                        <img src={logo} width={"80px"} alt="Logo de Attiva" />
                    </Link>
                </div>
                <HeaderMenuRight />
            </div>
        </StickyHeader>
    );
}
